/**
 * Hand-illustrated Earth surface texture.
 *
 * Replaces the previous noise-based continent painter with deliberate
 * cartoon continent silhouettes drawn via Canvas2D paths. The result
 * reads as a stylized illustration — recognizable continent blobs over
 * deep cartoon-blue ocean with bright polar caps — instead of generic
 * procedural noise.
 *
 * Coordinates are normalized [0..1] so the painter is resolution-agnostic.
 * The default 1024² canvas hits the GPU as a sharp 4× upgrade over the
 * old 256² procedural map.
 *
 * Falls back to a flat ocean DataTexture in headless test environments
 * where Canvas2D is not available.
 */

import { CanvasTexture, DataTexture, LinearFilter, RGBAFormat, type Texture } from "three";

const DEFAULT_SIZE = 1024;

const OCEAN_DEEP = "#1a64c4";
const OCEAN_SHALLOW = "#3d9beb";
const LAND_BASE = "#67c878";
const LAND_HIGHLIGHT = "#a8e08a";
const LAND_OUTLINE = "#1d3d28";
const POLAR_CAP = "#f4faff";
const CLOUD_TINT = "rgba(255,255,255,0.55)";

interface ContinentBlob {
  /** Center point in normalized [0..1] coords. */
  cx: number;
  cy: number;
  /** Hand-traced control points relative to (cx, cy), normalized. */
  shape: ReadonlyArray<readonly [number, number]>;
  /** Optional inner highlight blob (lighter mountains / interior). */
  highlight?: ReadonlyArray<readonly [number, number]>;
}

// Stylized continent silhouettes — not geographically accurate, but
// each blob reads as "continent-shaped" rather than noise. Layout
// loosely echoes Earth: big land mass left-of-center (eurasia +
// africa), americas on the right, oceania bottom.
const CONTINENTS: ReadonlyArray<ContinentBlob> = [
  // Eurasia + Africa fused into one large stylized blob.
  {
    cx: 0.30, cy: 0.45,
    shape: [
      [-0.18, -0.18], [-0.06, -0.22], [ 0.10, -0.20], [ 0.22, -0.10],
      [ 0.20, -0.02], [ 0.10,  0.06], [ 0.18,  0.10], [ 0.16,  0.22],
      [ 0.06,  0.30], [-0.04,  0.26], [-0.10,  0.16], [-0.18,  0.08],
      [-0.22, -0.02], [-0.20, -0.10],
    ],
    highlight: [
      [-0.06, -0.10], [ 0.08, -0.12], [ 0.10, -0.04], [ 0.00,  0.02],
      [-0.08, -0.02],
    ],
  },
  // Americas — vertical strip on the right.
  {
    cx: 0.72, cy: 0.50,
    shape: [
      [-0.05, -0.28], [ 0.05, -0.30], [ 0.12, -0.20], [ 0.06, -0.08],
      [ 0.10,  0.00], [ 0.04,  0.10], [ 0.10,  0.20], [ 0.04,  0.30],
      [-0.04,  0.32], [-0.10,  0.22], [-0.04,  0.10], [-0.10,  0.00],
      [-0.06, -0.10], [-0.10, -0.20],
    ],
    highlight: [
      [-0.02, -0.18], [ 0.04, -0.12], [ 0.00, -0.04], [-0.04, -0.10],
    ],
  },
  // Oceania — small island cluster bottom-center.
  {
    cx: 0.46, cy: 0.74,
    shape: [
      [-0.08, -0.04], [ 0.00, -0.06], [ 0.08, -0.02], [ 0.06,  0.04],
      [-0.02,  0.06], [-0.08,  0.02],
    ],
  },
  // Greenland-ish island top-left.
  {
    cx: 0.18, cy: 0.18,
    shape: [
      [-0.05, -0.04], [ 0.02, -0.06], [ 0.05,  0.00], [ 0.02,  0.05],
      [-0.04,  0.04], [-0.06,  0.00],
    ],
  },
];

function paintGradientOcean(
  ctx: CanvasRenderingContext2D,
  size: number,
): void {
  // Vertical band — deeper toward equator, lighter toward poles.
  // This subtle variation gives the ocean depth without losing the
  // cel-shaded flatness.
  const gradient = ctx.createLinearGradient(0, 0, 0, size);
  gradient.addColorStop(0.00, OCEAN_SHALLOW);
  gradient.addColorStop(0.45, OCEAN_DEEP);
  gradient.addColorStop(0.55, OCEAN_DEEP);
  gradient.addColorStop(1.00, OCEAN_SHALLOW);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
}

function paintPolarCaps(
  ctx: CanvasRenderingContext2D,
  size: number,
): void {
  // Top + bottom caps — wavy edges for cartoon feel.
  ctx.fillStyle = POLAR_CAP;
  for (const top of [true, false]) {
    ctx.beginPath();
    const baseY = top ? 0 : size;
    const peakY = top ? size * 0.10 : size * 0.90;
    const sign = top ? 1 : -1;
    ctx.moveTo(0, baseY);
    const seg = 16;
    for (let i = 0; i <= seg; i += 1) {
      const x = (i / seg) * size;
      const wave = Math.sin(i * 1.7) * size * 0.018;
      ctx.lineTo(x, peakY + sign * wave);
    }
    ctx.lineTo(size, baseY);
    ctx.closePath();
    ctx.fill();
  }
}

function strokeBlobPath(
  ctx: CanvasRenderingContext2D,
  size: number,
  cx: number,
  cy: number,
  pts: ReadonlyArray<readonly [number, number]>,
): void {
  if (pts.length === 0) return;
  ctx.beginPath();
  const [px0, py0] = pts[0]!;
  ctx.moveTo((cx + px0) * size, (cy + py0) * size);
  // Quadratic curves between successive points using their midpoints
  // as smooth anchors — gives the rounded cartoon silhouette.
  for (let i = 1; i < pts.length; i += 1) {
    const [px, py] = pts[i]!;
    const [pxNext, pyNext] = pts[(i + 1) % pts.length]!;
    const ax = (px + pxNext) / 2;
    const ay = (py + pyNext) / 2;
    ctx.quadraticCurveTo(
      (cx + px) * size,
      (cy + py) * size,
      (cx + ax) * size,
      (cy + ay) * size,
    );
  }
  ctx.closePath();
}

function paintContinents(
  ctx: CanvasRenderingContext2D,
  size: number,
): void {
  for (const blob of CONTINENTS) {
    // Outline — heavy dark stroke for the cartoon line-art look.
    strokeBlobPath(ctx, size, blob.cx, blob.cy, blob.shape);
    ctx.fillStyle = LAND_BASE;
    ctx.fill();
    ctx.strokeStyle = LAND_OUTLINE;
    ctx.lineWidth = size * 0.005;
    ctx.lineJoin = "round";
    ctx.stroke();

    if (blob.highlight) {
      // Inner mountains / interior highlight — lighter green blob.
      strokeBlobPath(ctx, size, blob.cx, blob.cy, blob.highlight);
      ctx.fillStyle = LAND_HIGHLIGHT;
      ctx.fill();
    }
  }
}

function paintCloudWisps(
  ctx: CanvasRenderingContext2D,
  size: number,
): void {
  // Sparse soft cloud bands — adds atmosphere life without obscuring
  // continents. Drawn last so they overlay everything.
  ctx.save();
  ctx.fillStyle = CLOUD_TINT;
  const wisps: ReadonlyArray<readonly [number, number, number, number]> = [
    // [cx, cy, w, h] all normalized.
    [0.55, 0.30, 0.22, 0.06],
    [0.20, 0.62, 0.16, 0.04],
    [0.85, 0.20, 0.12, 0.04],
    [0.42, 0.85, 0.20, 0.05],
  ];
  for (const [cx, cy, w, h] of wisps) {
    ctx.beginPath();
    ctx.ellipse(
      cx * size,
      cy * size,
      (w / 2) * size,
      (h / 2) * size,
      0,
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }
  ctx.restore();
}

export interface PaintEarthSurfaceOptions {
  size?: number;
}

export function paintEarthSurface(opts: PaintEarthSurfaceOptions = {}): Texture {
  const size = opts.size ?? DEFAULT_SIZE;
  if (typeof document !== "undefined") {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      paintGradientOcean(ctx, size);
      paintPolarCaps(ctx, size);
      paintContinents(ctx, size);
      paintCloudWisps(ctx, size);
      const tex = new CanvasTexture(canvas);
      tex.minFilter = LinearFilter;
      tex.magFilter = LinearFilter;
      return tex;
    }
  }
  // headless fallback — flat ocean blue, just enough to not break tests.
  const data = new Uint8Array(size * size * 4);
  for (let i = 0; i < data.length; i += 4) {
    data[i] = 0x3d;
    data[i + 1] = 0x9b;
    data[i + 2] = 0xeb;
    data[i + 3] = 0xff;
  }
  const tex = new DataTexture(data, size, size, RGBAFormat);
  tex.needsUpdate = true;
  return tex;
}

/**
 * F-096 — splash 등 외부에서 Earth surface 캔버스 그대로 가져갈 수 있게.
 * paintEarthSurface 와 같은 painting 을 적용하지만 Three.Texture 로 감싸지
 * 않고 HTMLCanvasElement 자체를 반환. 헤드리스 환경에선 null.
 */
export function paintEarthCanvas(size = DEFAULT_SIZE): HTMLCanvasElement | null {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  paintGradientOcean(ctx, size);
  paintPolarCaps(ctx, size);
  paintContinents(ctx, size);
  paintCloudWisps(ctx, size);
  return canvas;
}

export const __testing = {
  CONTINENTS,
  DEFAULT_SIZE,
  OCEAN_DEEP,
  LAND_BASE,
};
